import { Icon } from "@circle-react-shared/Icon";
import { Dropdown } from "@circle-react-shared/uikit/Dropdown";
import { popperSameWidthModifier } from "@circle-react-shared/uikit/Dropdown/utils";
import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { ModalManager } from "@circle-react/components/Layout/ModalManagerProvider";
import { useModal } from "@ebay/nice-modal-react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";

const REPORT_REASONS = [
  "harassment",
  "spam",
  "against_guidelines",
  "other",
] as const;

type ReportReasonType = (typeof REPORT_REASONS)[number];

export interface ReportFormData {
  reported_reason_body: string;
  reported_reason_type: string;
}

interface ReportConnectionModalProps {
  isLoading?: boolean;
  onSubmit: (data: ReportFormData) => void;
}

const ReasonDropdown = () => {
  const { setValue, watch } = useFormContext();
  const selectedReason = watch("reported_reason_type") as ReportReasonType;

  const getReasonLabel = (reason: ReportReasonType) =>
    t(`flagged_contents.report_reasons.${reason}`);

  return (
    <div className="w-full">
      <Dropdown
        direction="bottom-start"
        size="full"
        className="w-full"
        buttonClassName="w-full"
        buttonWrapperClassName="w-full"
        menuWrapperClassName="!px-0"
        menuItemsClassName="!w-full !p-2"
        popperOptions={{ modifiers: [popperSameWidthModifier] }}
        button={
          <Dropdown.MenuButton className="border-primary hover:border-dark flex h-10 w-full items-center justify-between rounded-lg border bg-transparent px-3 transition-colors">
            <span className="text-dark truncate text-sm">
              {getReasonLabel(selectedReason)}
            </span>
            <Icon
              size={16}
              type="20-chevron-down"
              className="text-light shrink-0"
            />
          </Dropdown.MenuButton>
        }
      >
        {REPORT_REASONS.map(reason => (
          <Dropdown.ItemWithLink
            key={reason}
            linkType="button"
            active={selectedReason === reason}
            onClick={() => setValue("reported_reason_type", reason)}
          >
            {getReasonLabel(reason)}
          </Dropdown.ItemWithLink>
        ))}
      </Dropdown>
    </div>
  );
};

const ReportConnectionModalComponent = ({
  onSubmit,
  isLoading = false,
}: ReportConnectionModalProps) => {
  const modal = useModal();

  const handleSubmit = (data: ReportFormData) => {
    onSubmit(data);
    void modal.hide();
  };

  return (
    <Modal isOpen={modal.visible} onClose={modal.hide}>
      <Modal.Overlay />
      <Modal.Content size="md">
        <Modal.Header>
          <Modal.Title size="sm">
            {t("profile_drawer.connect_banner.report_note.title")}
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={modal.hide} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Form
          defaultValues={{
            reported_reason_body: "",
            reported_reason_type: "spam",
          }}
          onSubmit={handleSubmit}
        >
          <Modal.Body>
            <Form.Item
              className="!pt-0"
              fullWidth
              hideBorder
              hideDescription
              label={t("flagged_contents.form.reported_reason_type")}
              name="reported_reason_type"
            >
              <ReasonDropdown />
            </Form.Item>
            <Form.Item
              fullWidth
              hideBorder
              hideDescription
              label={`${t("flagged_contents.form.reported_reason_body")} (${t("paywall_checkout.form.fields.optional").toLowerCase()})`}
              name="reported_reason_body"
            >
              <Form.TextArea name="reported_reason_body" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex items-center justify-end gap-2">
              <Button
                disabled={isLoading}
                onClick={modal.hide}
                type="button"
                variant="secondary"
              >
                {t("cancel")}
              </Button>
              <Button disabled={isLoading} type="submit" variant="primary">
                {t("report")}
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

const ReportConnectionModal = ModalManager.create(
  ({ onSubmit, isLoading }: ReportConnectionModalProps) => (
    <ReportConnectionModalComponent onSubmit={onSubmit} isLoading={isLoading} />
  ),
);

export const useReportConnectionModal = () => useModal(ReportConnectionModal);
